import { useState } from "react";
import { Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { TemplateModalForm } from "modules/ChecklistSettingsDashboard/components/TemplateModal/components";
import {
  useChecklistTemplateDetailsQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation
} from "modules/ChecklistSettingsDashboard/components/TemplateModal/hooks";
import "modules/ChecklistSettingsDashboard/components/TemplateModal/TemplateModal.scss";
import { toISOString } from "utils/dateHelperFunctions";

import type { DropdownItemProps } from "components";
import type { ChecklistTemplate } from "models";

type TemplateModalProps = {
  isOpen: boolean;
  templateID: number | null;
  reportTemplateOptions: DropdownItemProps[];
  onClose: () => void;
};

const isInvalidForm = ({ name }: ChecklistTemplate) => !name && name.trim().length === 0;

const FormHelper = createFormHelper<ChecklistTemplate>();

export const TemplateModal = ({ isOpen, onClose, templateID, reportTemplateOptions }: TemplateModalProps) => {
  const { data: templateDetails, isFetching: isFetchingTemplate } = useChecklistTemplateDetailsQuery(templateID);

  const { mutateAsync: createTemplate, isPending: isCreateTemplatePending } = useCreateTemplateMutation();
  const { mutateAsync: updateTemplate, isPending: isUpdateTemplatePending } = useUpdateTemplateMutation();

  const [createdTemplateId, setCreatedTemplateId] = useState<number | null>(null);

  const handleClose = () => {
    setCreatedTemplateId(null);
    onClose();
  };

  const handleCreateChecklistTemplate = async (payload: ChecklistTemplate) => {
    const now = toISOString(new Date());

    const id = createdTemplateId ?? (await createTemplate(payload.name));
    setCreatedTemplateId(id);
    await updateTemplate({ ...payload, id, created_on: now, updated_on: now });
    handleClose();
  };

  const handleSubmit = async (payload: ChecklistTemplate) => {
    if (isInvalidForm(payload)) return;

    if (templateID !== null) {
      await updateTemplate(payload);
      handleClose();
      return;
    }

    await handleCreateChecklistTemplate(payload);
  };

  return (
    <Modal open={isOpen} size="large" className="TemplateModal" closeOnDimmerClick={false}>
      <FormHelper.Form
        onSubmit={handleSubmit}
        defaultValues={{
          id: templateDetails?.id ?? -1,
          name: templateDetails?.name ?? "",
          description: templateDetails?.description ?? "",
          updated_on: templateDetails?.updated_on ?? "",
          created_on: templateDetails?.created_on ?? "",
          icon_color: templateDetails?.icon_color ?? "#000",
          text_color: templateDetails?.text_color ?? "#000",
          primary_color: templateDetails?.primary_color ?? "#000",
          pdf_layout_id: templateDetails?.pdf_layout_id ?? null,
          add_purpose: templateDetails?.add_purpose ?? false,
          pin_enabled: templateDetails?.pin_enabled ?? false,
          default_only: templateDetails?.default_only ?? false,
          price_enabled: templateDetails?.price_enabled ?? false,
          ready_enabled: templateDetails?.ready_enabled ?? false,
          hide_timestamp: templateDetails?.hide_timestamp ?? false,
          snooze_enabled: templateDetails?.snooze_enabled ?? false,
          is_previewable: templateDetails?.is_previewable ?? false,
          total_pdf_enabled: templateDetails?.total_pdf_enabled ?? false,
          multi_use_enabled: templateDetails?.multi_use_enabled ?? false,
          show_mechanic_name: templateDetails?.show_mechanic_name ?? false,
          include_in_reports: templateDetails?.include_in_reports ?? false,
          customer_ok_enabled: templateDetails?.customer_ok_enabled ?? false,
          cc_default_selected: templateDetails?.cc_default_selected ?? false,
          can_skip_km_and_date: templateDetails?.can_skip_km_and_date ?? false,
          cc_default_selected_nvt: templateDetails?.cc_default_selected_nvt ?? false,
          skip_update_appointment_status: templateDetails?.skip_update_appointment_status ?? false
        }}
      >
        <TemplateModalForm
          isFetchingTemplate={isFetchingTemplate}
          reportTemplateOptions={reportTemplateOptions}
          isEdit={templateID !== null}
          isPending={isCreateTemplatePending || isUpdateTemplatePending}
          onClose={handleClose}
        />
      </FormHelper.Form>
    </Modal>
  );
};
